import { useState, useRef, useEffect } from 'react';
import { Pencil } from 'lucide-react';

interface Props {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
}

export default function EditableTitle({ value, onChange, placeholder = 'Untitled Document' }: Props) {
  const [isEditing, setIsEditing] = useState(false);
  const [editValue, setEditValue] = useState(value);
  const [isHovered, setIsHovered] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    setEditValue(value);
  }, [value]);

  useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [isEditing]);

  const startEditing = () => {
    setEditValue(value);
    setIsEditing(true);
  };

  const save = () => {
    const trimmed = editValue.trim();
    if (trimmed && trimmed !== value) {
      onChange(trimmed);
    } else {
      setEditValue(value);
    }
    setIsEditing(false);
  };

  const cancel = () => {
    setEditValue(value);
    setIsEditing(false);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      save();
    } else if (e.key === 'Escape') {
      e.preventDefault();
      cancel();
    }
  };

  if (isEditing) {
    return (
      <input
        ref={inputRef}
        type="text"
        value={editValue}
        onChange={(e) => setEditValue(e.target.value)}
        onKeyDown={handleKeyDown}
        onBlur={save}
        placeholder={placeholder}
        className="text-lg font-semibold bg-transparent border-b-2 border-accent outline-none px-1 py-0.5 min-w-[200px] max-w-[400px] text-gray-800"
        style={{ width: `${Math.max(editValue.length, placeholder.length) * 10 + 20}px` }}
      />
    );
  }

  return (
    <div
      className="group flex items-center gap-2 cursor-pointer rounded px-1 py-0.5 hover:bg-gray-100 transition-colors"
      onClick={startEditing}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      title="Click to edit title"
    >
      <h1 className="text-lg font-semibold text-gray-800 truncate max-w-[350px]">
        {value || <span className="text-gray-400 italic">{placeholder}</span>}
      </h1>
      <Pencil
        size={14}
        className={`text-gray-400 transition-opacity duration-150 shrink-0 ${
          isHovered ? 'opacity-100' : 'opacity-0'
        }`}
      />
    </div>
  );
}
